import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Trophy, Loader2, CheckCircle2, KeyRound } from "lucide-react";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get("token") || "";
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (password.length < 6) {
      toast.dismiss();
      toast.error("Password must be at least 6 characters.");
      return;
    }
    if (password !== confirm) {
      toast.dismiss();
      toast.error("Passwords don't match.");
      return;
    }
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, password });
      setDone(true);
      toast.success("Password updated");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background relative flex items-center justify-center px-4 overflow-hidden">
      <div className="absolute inset-0 bg-background/60" />
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
        className="relative z-10 w-full max-w-md glass rounded-3xl p-8 sm:p-10"
        data-testid="reset-password-card"
      >
        <div className="flex flex-col items-center text-center mb-8">
          <div className="h-14 w-14 rounded-2xl bg-primary flex items-center justify-center shadow-xl shadow-primary/30 mb-4">
            <Trophy className="h-7 w-7 text-primary-foreground" />
          </div>
          <h1 className="font-display text-2xl font-extrabold tracking-tight">Reset password</h1>
          <p className="text-muted-foreground text-sm mt-1">Choose a new password for your account.</p>
        </div>

        {!token ? (
          <div className="text-center space-y-4" data-testid="reset-no-token">
            <p className="text-sm text-muted-foreground">
              This reset link is missing or invalid. Please request a new one.
            </p>
            <Button onClick={() => navigate("/auth")} className="rounded-full w-full h-11 font-semibold">
              Back to sign in
            </Button>
          </div>
        ) : done ? (
          <div className="text-center space-y-4" data-testid="reset-success">
            <CheckCircle2 className="h-12 w-12 mx-auto text-[#34C759]" />
            <p className="text-sm text-muted-foreground">
              Your password has been updated. You can now sign in.
            </p>
            <Button
              onClick={() => navigate("/auth")}
              data-testid="reset-goto-login"
              className="rounded-full w-full h-11 font-semibold"
            >
              Go to sign in
            </Button>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="new-password">New password</Label>
              <Input
                id="new-password"
                type="password"
                data-testid="new-password-input"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                placeholder="••••••••"
                className="rounded-xl h-11"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="confirm-password">Confirm password</Label>
              <Input
                id="confirm-password"
                type="password"
                data-testid="confirm-password-input"
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                required
                placeholder="••••••••"
                className="rounded-xl h-11"
              />
            </div>
            <Button
              type="submit"
              disabled={loading}
              data-testid="reset-submit"
              className="w-full h-11 rounded-full font-semibold text-base"
            >
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <KeyRound className="h-4 w-4" />}
              Update password
            </Button>
          </form>
        )}
      </motion.div>
    </div>
  );
}
